import { useAuth0 } from '@auth0/auth0-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MdArrowBack } from 'react-icons/md';
import { z } from 'zod';
import {
  Alert,
  Button,
  ErrorableLoader,
  LinkButton,
  TextInput,
} from '@src/components';
import Modal from '@src/components/Modal';
import {
  CurrentUserFragment,
  useResendEmailVerificationMutation,
  useUpdateUserDetailsMutation,
} from '@src/generated/graphql';
import { useLogOut } from '@src/hooks/useLogOut';
import { useToast } from '@src/hooks/useToast';
import { getCurrentLocale } from '@src/i18n';
import {
  getApolloErrorCode,
  logAndFormatError,
} from '@src/services/error-formatter';
import { logError } from '@src/services/error-logger';
import { useRegisterUser } from '../../hooks/useRegisterUser';

interface Props {
  user: CurrentUserFragment;
}

// keep in sync with backend/src/modules/account/user/services/user-register-service.ts
const formSchema = z.object({
  name: z
    .string()
    .min(1, 'Name must be at least 1 character')
    .max(30, 'Name must be fewer than 30 characters'),
});

type FormValues = z.infer<typeof formSchema>;

function UserRegisterNameForm({
  user,
  onSubmit,
}: {
  user: CurrentUserFragment;
  onSubmit: (name: string) => void;
}): JSX.Element {
  const { t } = useTranslation();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: user.name || '',
    },
    resolver: zodResolver(formSchema),
  });

  const handleSubmitName = (values: FormValues): void => {
    onSubmit(values.name);
  };

  return (
    <form
      onSubmit={handleSubmit(handleSubmitName)}
      className="flex flex-col items-center space-y-4"
    >
      <img className="h-10 w-10" alt="Hello" src="/images/wave.png" />
      <h4 className="text-center">
        {t('registration.name.welcome_header', 'Welcome!')}
        <br />
        {t(
          'registration.name.name_question_header',
          'What name should we address you by?',
        )}
      </h4>
      <TextInput.LabelledController
        className="w-full"
        placeholder={t('registration.name.nickname_label', 'Your nickname')}
        name="name"
        control={control}
      />
      <Button className="w-full justify-center text-center" type="submit">
        {t('buttons.next', 'Next')}
      </Button>
    </form>
  );
}

function SubscribeToMarketingForm({
  name,
  isEmailVerified,
  onNextStep,
  onBack,
}: {
  name: string;
  isEmailVerified: boolean;
  onBack: () => void;
  onNextStep: () => void;
}): JSX.Element {
  const { t } = useTranslation();

  const [error, setError] = useState<string | null>();
  const [updateUserDetails] = useUpdateUserDetailsMutation();
  const { registerUser } = useRegisterUser();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const handleSubmitMarketingSubscription = async (
    subscribed: boolean,
  ): Promise<void> => {
    try {
      setIsUpdating(true);
      // we also want to update the user's locale upon registration
      await updateUserDetails({
        variables: {
          input: {
            name,
            isSubscribedToMarketing: subscribed,
            locale: getCurrentLocale(),
          },
        },
      });
      if (isEmailVerified) {
        await registerUser();
      } else {
        onNextStep();
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div className="absolute left-0 top-0">
        <LinkButton onClick={onBack}>
          <MdArrowBack className="h-6 w-6 text-gray-600" />
          <span className="sr-only">{t('buttons.back')}</span>
        </LinkButton>
      </div>
      <img className="h-10 w-10" alt="Hello" src="/images/speech_balloon.png" />
      <h4 className="px-4 text-center">
        {t(
          'registration.marketing_subscription.header',
          'Would you like to receive email updates and information on new services from Bordr?',
        )}
      </h4>
      <div className="flex w-full space-x-4">
        <Button
          color="white"
          className="w-full justify-center"
          disabled={isUpdating}
          onClick={() => {
            handleSubmitMarketingSubscription(true).catch((err) =>
              setError(logAndFormatError(err)),
            );
          }}
        >
          {t('buttons.yes', 'Yes')}
        </Button>
        <Button
          color="white"
          className="w-full justify-center"
          disabled={isUpdating}
          onClick={() => {
            handleSubmitMarketingSubscription(false).catch((err) =>
              setError(logAndFormatError(err)),
            );
          }}
        >
          {t('buttons.no', 'No')}
        </Button>
      </div>
      {error && <Alert type="error">{error}</Alert>}
    </>
  );
}

const VERIFY_EMAIL_CHECK_INTERVAL = 15 * 1000; // every 15 seconds

function VerifyEmailScreen({
  user,
  isEmailVerified,
  recheckIsEmailVerified,
}: {
  user: CurrentUserFragment;
  isEmailVerified: boolean;
  recheckIsEmailVerified: () => Promise<void>;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const { registerUser } = useRegisterUser();
  const { t } = useTranslation();
  const toast = useToast();

  // register user if the email is verified
  useEffect(() => {
    if (!isEmailVerified) return;

    registerUser().catch((err) => setError(logAndFormatError(err)));
  }, [isEmailVerified, registerUser]);

  // check if the email is verified every 15 seconds
  useEffect(() => {
    if (isEmailVerified) return undefined;
    const interval = setInterval(() => {
      // this is a nice-to-have check (they can always refresh the page)
      // so we don't need to show an error
      recheckIsEmailVerified().catch((err) => logError(err));
    }, VERIFY_EMAIL_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [recheckIsEmailVerified, isEmailVerified]);

  const [resendEmailVerification] = useResendEmailVerificationMutation();
  const [isResending, setIsResending] = useState(false);

  const onResendEmailVerification = (): void => {
    setIsResending(true);
    resendEmailVerification()
      .then(() => toast.success('Successfully re-sent email verification!'))
      .catch((err) => {
        if (getApolloErrorCode(err) === 'already-verified') {
          toast.success(
            t(
              'registration.verify_email.already_verified',
              'Your email is already verified.',
            ),
          );
          return;
        }
        toast.error(logAndFormatError(err));
      })
      .finally(() => setIsResending(false));
  };

  const logOut = useLogOut();

  return (
    <div className="flex flex-col items-center space-y-6">
      <img
        className="h-10 w-10"
        alt="Hello"
        src="/images/envelope-with-arrow.png"
      />
      <h3 className="text-lg font-medium leading-6">
        {t('registration.verify_email.header', 'Verify your email')}
      </h3>
      {isEmailVerified ? (
        <ErrorableLoader error={error} />
      ) : (
        <>
          <p className="text-small text-center font-normal leading-5">
            {t(
              'registration.verify_email.email_sent',
              "An email has been sent to {{email}} with a link to activate your account. If you don't see it in your inbox within a few minutes, please check your spam folder.",
              {
                email: user.email,
              },
            )}
          </p>
          <Button
            disabled={isResending}
            className="w-full justify-center"
            onClick={onResendEmailVerification}
          >
            {t('buttons.resend_email', 'Resend email')}
          </Button>
          <div className="flex w-full justify-center space-x-4">
            <LinkButton
              color="blue"
              className="font-medium no-underline"
              onClick={() => logOut()}
            >
              {t('buttons.log_out')}
            </LinkButton>
          </div>
        </>
      )}
    </div>
  );
}

function UserRegisterModal({ user }: Props): JSX.Element {
  const initialStep =
    user.isSubscribedToMarketing == null ? 'name' : 'verifyEmail';
  const [currentStep, setCurrentStep] = useState<
    'name' | 'subscribe' | 'verifyEmail'
  >(initialStep);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState<boolean | null>(null);

  const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();

  const checkIsEmailVerified = useCallback(
    async (noCache?: boolean): Promise<void> => {
      if (noCache) {
        await getAccessTokenSilently({ cacheMode: 'off' });
      }
      const idClaims = await getIdTokenClaims();
      setIsEmailVerified(idClaims?.email_verified || false);
    },
    [getIdTokenClaims, getAccessTokenSilently],
  );

  useEffect(() => {
    checkIsEmailVerified().catch((err) => setError(logAndFormatError(err)));
  }, [checkIsEmailVerified]);

  const handleSubmitName = (newName: string): void => {
    setName(newName);
    setCurrentStep('subscribe');
  };

  return (
    <Modal
      size="medium"
      isOpen
      onClose={() => {
        /* no-op */
      }}
    >
      <div className="relative flex flex-col items-center space-y-4 py-4">
        {isEmailVerified === null ? (
          error && <Alert type="error">{error}</Alert>
        ) : (
          <>
            {currentStep === 'name' && (
              <UserRegisterNameForm user={user} onSubmit={handleSubmitName} />
            )}
            {currentStep === 'subscribe' && (
              <SubscribeToMarketingForm
                name={name}
                isEmailVerified={isEmailVerified}
                onNextStep={() => setCurrentStep('verifyEmail')}
                onBack={() => setCurrentStep('name')}
              />
            )}
            {currentStep === 'verifyEmail' && (
              <VerifyEmailScreen
                user={user}
                isEmailVerified={isEmailVerified}
                recheckIsEmailVerified={() => checkIsEmailVerified(true)}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default UserRegisterModal;
